import { useState } from 'react'
import { Head, Link, usePage, router } from '@inertiajs/react'
import Layout from '../../components/Layout'
import UserAvatar from '../../components/shared/UserAvatar'
import EditPersonModal from '../../components/people/EditPersonModal'
import DeactivateConfirmModal from '../../components/people/DeactivateConfirmModal'
import { PersonDetail, PersonSummary, ROLE_LABELS, UserRole, ActionItem, Rock, Metric, Topic } from '../../types'

interface Props {
  user: PersonDetail
  rocks: (Rock & { team_id: number; team_name: string | null })[]
  action_items: (ActionItem & { team_id: number; team_name: string | null })[]
  metrics: (Metric & { team_id: number; team_name: string | null })[]
  topics: (Topic & { team_id: number; team_name: string | null })[]
  direct_reports: PersonSummary[]
  manager: PersonSummary | null
  active_users: { id: number; name: string }[]
  assignable_roles: string[]
  can_edit: boolean
  can_deactivate: boolean
  can_delete: boolean
  can_set_password: boolean
  can_toggle_guide_privileges: boolean
}

type SectionTab = 'overview' | 'rocks' | 'action_items' | 'metrics' | 'topics' | 'reviews'

export default function PersonShow() {
  const props = usePage().props as unknown as Props
  const { user, rocks, action_items, metrics, topics, direct_reports, manager, active_users, assignable_roles, can_edit, can_deactivate, can_delete, can_set_password, can_toggle_guide_privileges } = props
  const isGuide = user.role === 'guide'
  const [activeSection, setActiveSection] = useState<SectionTab>('overview')
  const [showEditModal, setShowEditModal] = useState(false)
  const [showDeactivateModal, setShowDeactivateModal] = useState(false)

  const sections: { key: SectionTab; label: string; count?: number }[] = [
    { key: 'overview', label: 'Overview' },
    { key: 'rocks', label: 'Rocks', count: rocks.length },
    { key: 'action_items', label: 'Action Items', count: action_items.length },
    { key: 'metrics', label: 'Metrics', count: metrics.length },
    { key: 'topics', label: 'Topics', count: topics.length },
    { key: 'reviews', label: 'Reviews' },
  ]

  const statusColors: Record<string, string> = {
    open: 'bg-charcoal/10 text-text-primary',
    completed: 'bg-[rgba(21,83,46,0.15)] text-[#15532E]',
    dropped: 'bg-charcoal/10 text-text-secondary',
  }

  const pctColor = (pct: number): string => {
    if (pct >= 100) return '#1B7340'
    if (pct >= 75) return '#5BA84B'
    if (pct >= 50) return '#FBA737'
    if (pct >= 25) return '#F08A3A'
    return '#ED1C24'
  }
  const fmtPct = (p: number): string => (p % 1 === 0 ? `${p}%` : p.toFixed(1) + '%')

  return (
    <Layout>
      <Head title={user.full_name} />
      <div className="max-w-5xl mx-auto px-6 py-6">
        <Link href="/people" className="inline-flex items-center gap-1 text-sm text-text-secondary hover:text-text-primary transition-colors mb-4">
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back to People
        </Link>

        <div className="bg-white rounded-xl border border-stone/20 shadow-sm overflow-hidden mb-6">
          <div className="px-6 py-5">
            <div className="flex items-start gap-4">
              <UserAvatar
                user={{ avatar_display: user.avatar_text || user.initials, avatar_url: user.avatar_url, full_name: user.full_name }}
                size="lg"
              />
              <div className="flex-1">
                <div className="flex items-center gap-3">
                  <h1 className="text-xl font-bold text-text-primary font-heading">{user.full_name}</h1>
                  {user.is_executive_leadership && (
                    <span className="text-[12px] font-semibold bg-[#B87A20]/20 text-[#B87A20] px-2 py-0.5 rounded-full uppercase tracking-wide">ELT</span>
                  )}
                  <span className={`text-[12px] font-medium px-2 py-0.5 rounded-full ${
                    user.status === 'active' ? 'bg-[rgba(27,115,64,0.15)] text-[#1B7340]' : 'bg-charcoal/10 text-text-secondary'
                  }`}>
                    {user.status}
                  </span>
                </div>
                {user.title && <p className="text-sm text-text-secondary mt-0.5">{user.title}</p>}
                <div className="flex items-center gap-4 mt-2 text-sm text-text-secondary">
                  <span>{user.email}</span>
                  {user.phone && <span>{user.phone}</span>}
                  <span className="bg-stone/20 px-2 py-0.5 rounded-full">{ROLE_LABELS[user.role as UserRole]}</span>
                </div>
              </div>

              {(can_edit || can_deactivate || can_delete) && (
                <div className="flex items-center gap-2 shrink-0">
                  {can_edit && !isGuide && (
                    <button
                      onClick={() => setShowEditModal(true)}
                      className="px-3 py-1.5 text-sm font-medium text-text-primary border border-stone/30 rounded-lg hover:bg-stone/10 transition-colors"
                    >
                      Edit
                    </button>
                  )}
                  {can_deactivate && user.status === 'active' && (
                    <button
                      onClick={() => setShowDeactivateModal(true)}
                      className="px-3 py-1.5 text-sm font-medium text-[#B87A20] border border-[#B87A20]/30 rounded-lg hover:bg-[#B87A20]/10 transition-colors"
                    >
                      Deactivate
                    </button>
                  )}
                  {can_deactivate && user.status === 'deactivated' && (
                    <button
                      onClick={() => router.post(`/people/${user.id}/reactivate`)}
                      className="px-3 py-1.5 text-sm font-medium text-[#15532E] border border-[#15532E]/30 rounded-lg hover:bg-[rgba(21,83,46,0.15)] transition-colors"
                    >
                      Reactivate
                    </button>
                  )}
                  {can_delete && (
                    <Link
                      href={`/people/${user.id}/deletion_manifest`}
                      className="px-3 py-1.5 text-sm font-medium text-pinnacle-red border border-pinnacle-red/30 rounded-lg hover:bg-pinnacle-red/10 transition-colors"
                    >
                      Delete
                    </Link>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {isGuide && (
          <div className="bg-white rounded-xl border border-stone/20 shadow-sm px-6 py-5 mb-6">
            <div className="flex items-start justify-between gap-6">
              <div>
                <h2 className="text-sm font-bold uppercase tracking-widest text-text-muted mb-1">Guide Settings</h2>
                <p className="text-sm text-text-secondary">
                  Guide details (name, email, role) are managed by Pinnacle administrators and are read-only here.
                </p>
              </div>
              {can_toggle_guide_privileges && (
                <label className="flex items-start gap-3 cursor-pointer select-none shrink-0 max-w-sm">
                  <input
                    type="checkbox"
                    checked={!!user.guide_user_privileges}
                    onChange={() => router.patch(`/people/${user.id}/guide_privileges`, { guide_user_privileges: !user.guide_user_privileges }, { preserveScroll: true })}
                    className="w-5 h-5 mt-0.5 rounded border-stone/30 text-pinnacle-red focus:ring-pinnacle-red/20"
                  />
                  <span>
                    <span className="block text-sm font-medium text-text-primary">Guide user privileges</span>
                    <span className="block text-sm text-text-muted">
                      Allow this guide to own Topics, Rocks, and Action Items and appear in meeting attendance for this account.
                    </span>
                  </span>
                </label>
              )}
              {!can_toggle_guide_privileges && user.guide_user_privileges && (
                <span className="text-[12px] font-semibold bg-charcoal/15 text-text-primary px-2 py-0.5 rounded-full uppercase tracking-wide shrink-0">User privileges</span>
              )}
            </div>
          </div>
        )}

        <div className="flex items-center gap-1 mb-4 border-b border-stone/20">
          {sections.map(s => (
            <button
              key={s.key}
              onClick={() => setActiveSection(s.key)}
              className={`px-4 py-2.5 text-sm font-medium transition-colors relative ${
                activeSection === s.key ? 'text-text-primary' : 'text-text-secondary hover:text-text-primary'
              }`}
            >
              {s.label}
              {s.count !== undefined && (
                <span className={`ml-1.5 text-sm px-1.5 py-0.5 rounded-full ${
                  activeSection === s.key ? 'bg-charcoal/10 text-text-primary' : 'bg-stone/20 text-text-muted'
                }`}>
                  {s.count}
                </span>
              )}
              {activeSection === s.key && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-pinnacle-red" />
              )}
            </button>
          ))}
        </div>

        <div className="bg-white rounded-xl border border-stone/20 shadow-sm overflow-hidden">
          {activeSection === 'overview' && (
            <div className="p-6 space-y-6">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <div>
                  <p className="text-sm text-text-muted mb-1">Manager</p>
                  {manager ? (
                    <Link href={`/people/${manager.id}`} className="text-sm text-text-primary hover:underline">{manager.full_name}</Link>
                  ) : (
                    <p className="text-sm text-text-secondary">None</p>
                  )}
                </div>
                <div>
                  <p className="text-sm text-text-muted mb-1">Teams</p>
                  <p className="text-sm text-text-primary">
                    {user.teams.length > 0 ? user.teams.map(t => t.name).join(', ') : 'None'}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-text-muted mb-1">Member Since</p>
                  <p className="text-sm text-text-primary">{new Date(user.created_at).toLocaleDateString()}</p>
                </div>
              </div>

              {direct_reports.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-text-primary mb-3">Direct Reports ({direct_reports.length})</h3>
                  <div className="divide-y divide-stone/10 border border-stone/20 rounded-lg overflow-hidden">
                    {direct_reports.map(report => (
                      <Link
                        key={report.id}
                        href={`/people/${report.id}`}
                        className="flex items-center gap-3 px-4 py-3 hover:bg-stone/5 transition-colors"
                      >
                        <div className="w-7 h-7 rounded-full bg-stone/20 flex items-center justify-center text-sm font-medium text-text-secondary shrink-0">
                          {report.initials}
                        </div>
                        <div className="flex-1 min-w-0">
                          <span className="text-sm text-text-primary font-medium">{report.full_name}</span>
                          {report.title && <span className="text-sm text-text-secondary ml-2">{report.title}</span>}
                        </div>
                        <span className="text-sm text-text-muted">{report.teams.map(t => t.name).join(', ')}</span>
                      </Link>
                    ))}
                  </div>
                </div>
              )}

              {direct_reports.length === 0 && (
                <p className="text-sm text-text-muted">No direct reports</p>
              )}
            </div>
          )}

          {activeSection === 'rocks' && (
            rocks.length === 0 ? (
              <div className="px-6 py-10 text-center text-sm text-text-secondary">No active rocks</div>
            ) : (
              <div className="divide-y divide-stone/10">
                {rocks.map(rock => {
                  const pct = rock.percent_complete ?? 0
                  const c = pctColor(pct)
                  return (
                    <Link
                      key={rock.id}
                      href={`/teams/${rock.team_id}/rocks`}
                      className="flex items-center gap-3 px-4 py-3 hover:bg-stone/5 transition-colors"
                    >
                      <span
                        className="text-[12px] font-bold px-2 py-0.5 rounded-full flex-shrink-0 whitespace-nowrap tabular-nums"
                        style={{ backgroundColor: `${c}26`, color: c }}
                      >
                        {fmtPct(pct)}
                      </span>
                      <span className="text-sm text-text-primary flex-1 truncate">{rock.title}</span>
                      {rock.team_name && <span className="text-sm text-text-muted">{rock.team_name}</span>}
                    </Link>
                  )
                })}
              </div>
            )
          )}

          {activeSection === 'action_items' && (
            action_items.length === 0 ? (
              <div className="px-6 py-10 text-center text-sm text-text-secondary">No action items</div>
            ) : (
              <div className="divide-y divide-stone/10">
                {action_items.map(item => (
                  <Link
                    key={item.id}
                    href={`/teams/${item.team_id}/action_items`}
                    className="flex items-center gap-3 px-4 py-3 hover:bg-stone/5 transition-colors"
                  >
                    <span className={`text-[12px] font-medium px-2 py-0.5 rounded-full ${statusColors[item.status] || ''}`}>
                      {item.status}
                    </span>
                    <span className="text-sm text-text-primary flex-1 truncate">{item.title}</span>
                    {item.due_date && (
                      <span className={`text-sm ${item.overdue ? 'text-pinnacle-red font-medium' : 'text-text-muted'}`}>
                        {new Date(item.due_date).toLocaleDateString()}
                      </span>
                    )}
                    {item.team_name && <span className="text-sm text-text-muted">{item.team_name}</span>}
                  </Link>
                ))}
              </div>
            )
          )}

          {activeSection === 'metrics' && (
            metrics.length === 0 ? (
              <div className="px-6 py-10 text-center text-sm text-text-secondary">No active metrics</div>
            ) : (
              <div className="divide-y divide-stone/10">
                {metrics.map(metric => (
                  <Link
                    key={metric.id}
                    href={`/teams/${metric.team_id}/scoreboard`}
                    className="flex items-center gap-3 px-4 py-3 hover:bg-stone/5 transition-colors"
                  >
                    <span className="text-sm text-text-primary flex-1 truncate">{metric.name}</span>
                    {metric.team_name && <span className="text-sm text-text-muted">{metric.team_name}</span>}
                  </Link>
                ))}
              </div>
            )
          )}

          {activeSection === 'topics' && (
            topics.length === 0 ? (
              <div className="px-6 py-10 text-center text-sm text-text-secondary">No topics</div>
            ) : (
              <div className="divide-y divide-stone/10">
                {topics.map(topic => (
                  <Link
                    key={topic.id}
                    href={`/teams/${topic.team_id}/topics`}
                    className="flex items-center gap-3 px-4 py-3 hover:bg-stone/5 transition-colors"
                  >
                    <span className="text-sm text-text-primary flex-1 truncate">{topic.title}</span>
                    <span className="text-sm text-text-muted">{topic.votes_count} votes</span>
                    {topic.team_name && <span className="text-sm text-text-muted">{topic.team_name}</span>}
                  </Link>
                ))}
              </div>
            )
          )}

          {activeSection === 'reviews' && (
            <div className="px-6 py-12 text-center">
              <svg className="w-10 h-10 text-text-primary/20 mx-auto mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <p className="text-sm font-medium text-text-muted mb-1">Reviews coming soon</p>
              <p className="text-sm text-text-muted">Performance reviews and 1:1 summaries will appear here.</p>
            </div>
          )}
        </div>
      </div>

      {showEditModal && (
        <EditPersonModal
          user={user}
          managers={active_users}
          assignableRoles={assignable_roles}
          canSetPassword={can_set_password}
          onClose={() => setShowEditModal(false)}
        />
      )}

      {showDeactivateModal && (
        <DeactivateConfirmModal
          user={user}
          onClose={() => setShowDeactivateModal(false)}
        />
      )}
    </Layout>
  )
}
